import { adminAuditDataRegistry } from "./data/admin.js";
import { formAuditDataRegistry } from "./data/form.js";
import { integrationsAuditDataRegistry } from "./data/integrations.js";
import { interviewAuditDataRegistry } from "./data/interview.js";
import { patientAuditDataRegistry } from "./data/patient.js";
import { s132AuditDataRegistry } from "./data/s132.js";
import { section12AuditDataRegistry } from "./data/section12.js";
import { userAuditDataRegistry } from "./data/user.js";
import { visionAuditDataRegistry } from "./data/vision.js";
import { workAuditDataRegistry } from "./data/work.js";
/**
 * Per-type Zod schemas for audit `data`, keyed by audit type. Each `data/*.ts` file
 * defines the schemas for one feature area and exports its own `…AuditDataRegistry`
 * (the audit-type → schema map for that area); this file just spreads them together.
 *
 * Audit `data` is a historical snapshot: a row records what was written when the
 * event happened, which may predate the current types (renamed enum values, removed
 * or since-retyped fields). So these schemas describe what was *written*, not the
 * live domain model, and are deliberately lenient — a superset of every writer;
 * unknown keys stripped; enum-like fields typed as `z.string()` (a renamed/legacy
 * value must not reject the row); payloads we don't model field-by-field typed as
 * `z.unknown()`. A field is `.nullish()` by default (NOT bare `.optional()`): audit
 * `data` is JSONB, so a field that is absent on the domain object persists as an
 * explicit `null` (SQL NULL read back by the DB, preserved through JSON.stringify),
 * which `.optional()` alone rejects — `.nullish()` accepts both `null` and absent.
 * Make a field required only when a read genuinely needs it, because a required field
 * missing from an older row makes the whole row read back imprecise. Never tighten a
 * schema to match today's types for the same reason (see `tryGetAuditData`). Keeping the `data/*.ts` files to zod +
 * primitives also makes them dependency-free leaves, avoiding import cycles into the
 * domain types.
 *
 * Write shapes are enforced only on direct `audit<T>(...)` calls, via the
 * object-literal excess-check against `AuditDataFor<T>`. Writes routed through a
 * `data: any` wrapper (e.g. `auditWorkItem`, `auditOrg`) bypass that check.
 *
 * Every `AuditTrailEntryType` is registered, including types with no current writer
 * (modelled empty) — a type having no writer today doesn't mean it has no stored rows
 * (past writers) or never will (future ones). The `Record<string, unknown>` fallback in
 * `AuditDataFor` and the `unmigrated` branch in `tryGetAuditData` therefore only fire for an
 * enum member added without a schema — a defensive net, not a normal case.
 *
 * `satisfies Partial<Record<...>>` (on each sub-registry and on the composed result,
 * rather than a mapped-type annotation) is load-bearing: it checks the keys without
 * widening the value types, so `typeof auditDataSchemaRegistry` keeps each key bound
 * to its concrete schema and `AuditDataFor` can infer the precise data shape. Keys
 * MUST be `AuditTrailEntryType` members, not plain strings, or `keyof` collapses to
 * `string` and inference is lost.
 */
// TODO: audit `data` is excluded from the anonymiser allowlist
// (anonymisation/configs/auditTrail.ts), so payloads are dropped to `{}` in
// anonymised copies, and some staff identifiers (phone numbers, gmcNumber, emails)
// are stored unredacted — review the redaction/anonymisation posture for audit data.
export const auditDataSchemaRegistry = {
    ...section12AuditDataRegistry,
    ...patientAuditDataRegistry,
    ...s132AuditDataRegistry,
    ...formAuditDataRegistry,
    ...workAuditDataRegistry,
    ...visionAuditDataRegistry,
    ...interviewAuditDataRegistry,
    ...userAuditDataRegistry,
    ...adminAuditDataRegistry,
    ...integrationsAuditDataRegistry,
};
//# sourceMappingURL=registry.js.map